import { APIProvider, AdvancedMarker, Map, useMap } from '@vis.gl/react-google-maps';
import { MapPin } from './MapPin';
import { useFitBoundsToStations } from './useFitBoundsToStations';
import { FALLBACK_CENTER, FALLBACK_ZOOM, HOME_MAP_ID, MAP_ID } from './mapConstants';
import type { StationDetailOut } from '@/api/stations';

interface Coords {
  lat: number;
  lng: number;
}

interface Props {
  apiKey: string;
  stations: StationDetailOut[];
  userCoords: Coords | null;
  selectedStationId: string | null;
  nearestStationId: string | null;
  /** Sheet expandida ocupa mais altura — o FAB de localizar sobe pra não
   * ficar atrás dela (mesma ideia de `mapfirst-a.jsx`: 2 offsets fixos, um
   * por estado da sheet, não medição dinâmica). */
  sheetExpanded: boolean;
  onSelectStation: (stationId: string) => void;
}

const LOCATE_FAB_BOTTOM = { peek: 190, expanded: 440 };

function LocateFab({ userCoords, sheetExpanded }: { userCoords: Coords | null; sheetExpanded: boolean }) {
  const map = useMap(HOME_MAP_ID);
  if (!userCoords) return null;
  return (
    <button
      type="button"
      onClick={() => {
        if (!map) return;
        map.panTo(userCoords);
        map.setZoom(Math.max(map.getZoom() ?? 0, 15));
      }}
      aria-label="Centralizar na minha localização"
      className="absolute right-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white text-bow-royal"
      style={{
        bottom: sheetExpanded ? LOCATE_FAB_BOTTOM.expanded : LOCATE_FAB_BOTTOM.peek,
        boxShadow: '0 4px 14px rgba(0,27,116,0.14)'
      }}
    >
      <LocateIcon />
    </button>
  );
}

function LocateIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="7" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
    </svg>
  );
}

function MapEffects({
  stations,
  userCoords
}: {
  stations: StationDetailOut[];
  userCoords: Coords | null;
}) {
  useFitBoundsToStations(HOME_MAP_ID, stations, userCoords);
  return null;
}

function YouAreHereDot({ coords }: { coords: Coords }) {
  return (
    <AdvancedMarker position={coords} zIndex={1}>
      <span className="relative flex h-[15px] w-[15px]">
        <span className="absolute inset-[-9px] rounded-full bg-bow-blue/25 animate-ping" />
        <span className="relative h-[15px] w-[15px] rounded-full border-[2.5px] border-white bg-bow-blue shadow-[0_2px_8px_rgba(0,27,116,0.3)]" />
      </span>
    </AdvancedMarker>
  );
}

/** Mapa em tela cheia da Home — estações reais como pins da Bow + posição do
 * usuário. Só apresentação: quem chama já resolve `apiKey`/`stations`/
 * `userCoords` (nenhum fetch acontece aqui). */
export function StationMap({
  apiKey,
  stations,
  userCoords,
  selectedStationId,
  nearestStationId,
  sheetExpanded,
  onSelectStation
}: Props) {
  return (
    <APIProvider apiKey={apiKey}>
      <div className="relative h-full w-full">
        <Map
          id={HOME_MAP_ID}
          mapId={MAP_ID}
          defaultCenter={FALLBACK_CENTER}
          defaultZoom={FALLBACK_ZOOM}
          disableDefaultUI
          gestureHandling="greedy"
          style={{ width: '100%', height: '100%' }}
        >
          <MapEffects stations={stations} userCoords={userCoords} />
          {userCoords && <YouAreHereDot coords={userCoords} />}
          {stations.map((s) => (
            <MapPin
              key={s.id}
              station={s}
              selected={s.id === selectedStationId}
              isNearest={s.id === nearestStationId}
              onClick={() => onSelectStation(s.id)}
            />
          ))}
        </Map>
        <LocateFab userCoords={userCoords} sheetExpanded={sheetExpanded} />
      </div>
    </APIProvider>
  );
}
